import { Injectable } from '@angular/core';

import { ObjectCheckerService } from '../../../../../services/object-checker.service';
import { StageService } from '../../../../../services/stage.service';
import { CollisionService } from '../../../../../services/collision/collision.service';
import { WallCutService } from '../../../../../services/wall/wall-cut.service';
import { ConstantsService } from '../../../../../services/constants.service';
import { CameraService } from '../../../../../services/camera.service';
import { WallObjectHandler } from './WallObjectHandler';
import { getWallSize } from '../../../../../services/wall/utils/get-wall-size';
import { wallContainsEmbeddedObject } from '../../../../../services/wall/utils/wall-contains-embedded-object';

declare const THREE: any;

@Injectable()
export class WallEmbeddableObjectHandler extends WallObjectHandler {
  private _wallContainObjectChecker: any;

  constructor(
    protected objectCheckerSrvc: ObjectCheckerService,
    protected stageService: StageService,
    protected collisionSrvc: CollisionService,
    protected wallCutSrvc: WallCutService,
    protected constants: ConstantsService,
    protected cameraSrvs: CameraService,
    private constantsService: ConstantsService
  ) {
    super(stageService);
  }

  protected _calculateNewPosition(wall, wallIntersectionPoint, objectEntity) {
    wall.getWorldDirection(this._wallDirection);
    if (this.cameraSrvs.isIn2D()) {
      this._position.copy(wall.position);
      wall.parent.localToWorld(this._position);
      const offset = this._position.sub(wallIntersectionPoint);
      offset.projectOnVector(this._wallDirection);
      this._position.copy(wallIntersectionPoint.add(offset));
    } else {
      const wallSize = getWallSize(wall);
      const offset = this._wallDirection.multiplyScalar(wallSize.z / 2);
      this._position.copy(wallIntersectionPoint.sub(offset));
    }
    const floorOffset = this.constants.deckHeight + (objectEntity.defaultHeightFromFloor || 0);
    this._position.y = floorOffset + objectEntity.height / 2;
    const floorPosition = wall.parent.getWorldPosition(new THREE.Vector3());

    const floorLevelCorrection = floorPosition.y - this.constantsService.deckHeight;
    switch (objectEntity.description) {
      case 'Single Door':
      case 'Double Door':
      case 'Double Sliding Door':
      case 'Single Window 2':
        this._position.y += floorLevelCorrection;
        break;
      case 'Single Window':
      case 'Double Window':
        this._position.y += floorLevelCorrection + this.constantsService.windowHeightAboveFloor;
        break;
      case 'sliding door':
        this._position.y += floorLevelCorrection + this.constantsService.windowAwningHeightAboveFloor;
        break;
      default:
        // Generic models: defaultHeightFromFloor from the product record is already applied
        // above; windows without one get the standard sill height.
        this._position.y += floorLevelCorrection;
        if (!objectEntity.defaultHeightFromFloor && objectEntity.productType === 'Window') {
          this._position.y += this.constantsService.windowHeightAboveFloor;
        }
        break;
    }

    return this._position;
  }

  canExecute(entity) {
    return this.objectCheckerSrvc.isWallEmbeddable(entity);
  }

  dragBegin(intersection) {
    const { object: wall, point: intersectionPoint } = intersection[0];
    const staticObjs = wall.children;
    const ghostObject = this.ghostObject;
    let handled = false;
    const entity = ghostObject.userData.entity;
    const position = this._calculateNewPosition(wall, intersectionPoint, entity);

    const collided = this.collisionSrvc.isCollide(ghostObject, staticObjs, {
      newPosition: position
    });

    const insideWall = wallContainsEmbeddedObject(
      wall,
      entity,
      position,
      new THREE.Euler(0, wall.rotation.y, 0)
    );

    if (!collided && insideWall) {
      handled = true;
      ghostObject.userData.wallId = wall.id;
      ghostObject.rotation.y = wall.rotation.y;
      ghostObject.position.copy(position);
      ghostObject.userData.parent = wall;
    }

    return handled;
  }

  dragEnd(object) {
    this.wallCutSrvc.cutHoles(object.parent);
  }
}



// WEBPACK FOOTER //
// ./src/app/Component(2D and 3D)/3D/ObjectAdd/Handlers/wallHandlers.ts/WallEmbeddableObjectHandler.ts